import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DocumentProvider } from './contexts/DocumentContext';
import { UpdateProvider } from './contexts/UpdateContext';
import { Navbar } from './components/Navbar';
import { AuthModal } from './components/AuthModal';
import { Marketplace } from './components/Marketplace';
import { CourseCreator } from './components/CourseCreator';
import { CoursePlayer } from './components/CoursePlayer';
import { LandingPage } from './components/LandingPage';
import { OrganisationDashboard } from './components/OrganisationDashboard';
import { DashboardHub } from './components/DashboardHub';
import AdminDashboard from './components/AdminDashboard';
import AcceptInvite from './components/AcceptInvite';
import { DocumentLibrary } from './components/DocumentLibrary';
import { SourceSets } from './components/SourceSets';
import { SourceSetDetail } from './components/SourceSetDetail';
import { SourceSetSummary } from './components/SourceSetSummary';
import { SourceSetAsk } from './components/SourceSetAsk';
import { RoleSelection } from './components/RoleSelection';
import { WelcomeActionScreen } from './components/WelcomeActionScreen';
import { DiagnosticsPage } from './pages/DiagnosticsPage';
import { Loader2, AlertTriangle, RefreshCcw, Mail } from 'lucide-react';
import { auth } from './lib/firebase';

// Theme Context for easy access if needed (optional since we use data-theme)
import { FeedbackButton } from './components/FeedbackButton';
import { logAppError } from './lib/errorLogger';

export type Theme = 'light' | 'dark';

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    logAppError('react_crash', error.message, { route: window.location.pathname });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-bg-main flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-bg-alt border border-rose-500/20 rounded-3xl p-8 text-center shadow-2xl">
            <div className="w-16 h-16 bg-rose-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-rose-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-4">Something went wrong</h2>
            <p className="text-text-alt mb-8 text-sm leading-relaxed">
              We encountered an error while rendering this page. This could be due to a data mismatch or a temporary issue.
            </p>
            <div className="bg-bg-main rounded-xl p-4 mb-8 text-left overflow-auto max-h-32 border border-border-main">
              <code className="text-xs text-rose-500 font-mono break-all">{this.state.error?.message}</code>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-500/20"
            >
              <RefreshCcw className="w-5 h-5" />
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function ProtectedRoute({ children, requiredRole, requirePlatformAdmin, allowProfessional }: { children: React.ReactNode, requiredRole?: 'learner' | 'tutor', requirePlatformAdmin?: boolean, allowProfessional?: boolean }) {
  const { user, role, userIntent, hasTutorAccess, isPlatformAdmin, loading, resendVerificationEmail, authError, clearAuthError, setAuthError } = useAuth();
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg-main">
        <Loader2 className="h-12 w-12 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  if (!user.emailVerified) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main">
        <div className="bg-bg-alt border border-border-main p-8 rounded-3xl max-w-md shadow-2xl">
          <div className="h-16 w-16 bg-indigo-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Mail className="h-8 w-8 text-indigo-500" />
          </div>
          <h2 className="text-2xl font-bold text-text-main mb-3">Verify your email</h2>
          <p className="text-text-alt mb-6 leading-relaxed">
            We sent a verification link to <strong className="text-text-main">{user.email}</strong>.
            Please check your inbox (and spam folder) and click the link to verify your account.
          </p>

          {authError && (
            <div className="mb-4 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-sm text-rose-500 text-left">
              {authError}
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-sm text-emerald-500 text-left">
              {successMsg}
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={async () => {
                clearAuthError();
                setSuccessMsg(null);
                try {
                  await resendVerificationEmail();
                  setSuccessMsg('Verification email sent! Please check your inbox.');
                } catch (err) {}
              }}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-indigo-500/20"
            >
              Resend Verification Email
            </button>
            <button
              onClick={async () => {
                clearAuthError();
                setSuccessMsg(null);
                const firebaseUser = auth.currentUser;
                if (firebaseUser) {
                  try {
                    await firebaseUser.reload();
                    if (firebaseUser.emailVerified) {
                      setSuccessMsg('Email verified successfully!');
                      window.location.reload();
                    } else {
                      setAuthError('Email is not verified yet. Please check your inbox and click the verification link.');
                    }
                  } catch (err) {
                    setAuthError('Failed to verify email status. Please try again later.');
                  }
                }
              }}
              className="w-full py-3 bg-bg-main hover:bg-bg-alt border border-border-main text-text-main font-bold rounded-xl transition-all"
            >
              I verified my email
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requiredRole === 'tutor' && !hasTutorAccess) {
    if (allowProfessional && userIntent === 'professional') {
      // allow
    } else {
      console.log('Restricted route blocked');
      return (
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main">
          <div className="bg-bg-alt border border-border-main p-8 rounded-3xl max-w-md shadow-2xl">
            <div className="h-16 w-16 bg-rose-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-rose-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Access Denied</h2>
            <p className="text-text-alt mb-6 leading-relaxed">
              You do not have permission to access this page.
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }
  }

  const hasAllowedTutorRouteAccess =
    requiredRole === 'tutor' &&
    (
      hasTutorAccess ||
      (allowProfessional && userIntent === 'professional')
    );

  if (requiredRole && role !== requiredRole && !hasAllowedTutorRouteAccess) {
    return <Navigate to="/" />;
  }

  if (requirePlatformAdmin && !isPlatformAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main">
        <div className="bg-bg-alt border border-border-main p-8 rounded-3xl max-w-md shadow-2xl">
          <div className="h-16 w-16 bg-rose-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="h-8 w-8 text-rose-500" />
          </div>
          <h2 className="text-2xl font-bold text-text-main mb-3">Access Denied</h2>
          <p className="text-text-alt mb-6 leading-relaxed">
            You do not have permission to access System Admin features. Platform administrator rights required.
          </p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function MainContent() {
  const { user, role, globalRole, loading, resendVerificationEmail, authError, clearAuthError, setAuthError } = useAuth();
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [hasSeenWelcome, setHasSeenWelcome] = useState(() => {
    return localStorage.getItem('lumina_welcome_seen') === 'true';
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg-main">
        <Loader2 className="h-12 w-12 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  if (!user.emailVerified) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main">
        <div className="bg-bg-alt border border-border-main p-8 rounded-3xl max-w-md shadow-2xl">
          <div className="h-16 w-16 bg-indigo-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Mail className="h-8 w-8 text-indigo-500" />
          </div>
          <h2 className="text-2xl font-bold text-text-main mb-3">Verify your email</h2>
          <p className="text-text-alt mb-6 leading-relaxed">
            We sent a verification link to <strong className="text-text-main">{user.email}</strong>.
            Please check your inbox (and spam folder) and click the link to verify your account.
          </p>

          {authError && (
            <div className="mb-4 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-sm text-rose-500 text-left">
              {authError}
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-sm text-emerald-500 text-left">
              {successMsg}
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={async () => {
                clearAuthError();
                setSuccessMsg(null);
                try {
                  await resendVerificationEmail();
                  setSuccessMsg('Verification email sent! Please check your inbox.');
                } catch (err) {}
              }}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-indigo-500/20"
            >
              Resend Verification Email
            </button>
            <button
              onClick={async () => {
                clearAuthError();
                setSuccessMsg(null);
                const firebaseUser = auth.currentUser;
                if (firebaseUser) {
                  try {
                    await firebaseUser.reload();
                    if (firebaseUser.emailVerified) {
                      setSuccessMsg('Email verified successfully!');
                      window.location.reload();
                    } else {
                      setAuthError('Email is not verified yet. Please check your inbox and click the verification link.');
                    }
                  } catch (err) {
                    setAuthError('Failed to verify email status. Please try again later.');
                  }
                }
              }}
              className="w-full py-3 bg-bg-main hover:bg-bg-alt border border-border-main text-text-main font-bold rounded-xl transition-all"
            >
              I verified my email
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!globalRole) {
    const signupIntent = sessionStorage.getItem('lumina_signup_intent');
    return <RoleSelection signupIntent={signupIntent} />;
  }

  if (!hasSeenWelcome) {
    return <WelcomeActionScreen onContinue={() => {
      localStorage.setItem('lumina_welcome_seen', 'true');
      setHasSeenWelcome(true);
    }} />;
  }

  if (role === 'tutor') {
    return <DashboardHub />;
  }

  return <Marketplace />;
}

export default function App() {
  const [theme, setTheme] = useState<Theme>(() => {
    return (localStorage.getItem('lumina_theme') as Theme) || 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('lumina_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    // Dispatch a custom event for Navbar to pick up if needed, though they share state via App context if we had one
    // But since Navbar is a child, we can pass it down or use window event
    window.dispatchEvent(new CustomEvent('themeChanged', { detail: newTheme }));
  };

  // Set up listener for the Navbar toggle
  useEffect(() => {
    const handleThemeToggle = (e: any) => {
      if (e.detail) setTheme(e.detail);
    };
    window.addEventListener('themeChanged', handleThemeToggle);
    return () => window.removeEventListener('themeChanged', handleThemeToggle);
  }, []);

  return (
    <DocumentProvider>
      <AuthProvider>
        <UpdateProvider>
          <Router>
            <div className="min-h-screen bg-bg-main text-text-main font-sans selection:bg-indigo-500/30">
              <Navbar />
              <AuthModal />
              <AuthErrorBanner />
              <SystemErrorBanner />
              <FirestoreOfflineBanner />
              <FeedbackButton />
              <main>
                <ErrorBoundary>
                  <Routes>
                    <Route path="/" element={<MainContent />} />
                    <Route
                      path="/course/:courseId"
                      element={
                        <ProtectedRoute>
                          <CoursePlayer />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute requiredRole="tutor">
                          <DashboardHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/courses/manage"
                      element={
                        <ProtectedRoute requiredRole="tutor" allowProfessional={true}>
                          <CourseCreator />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/organisation"
                      element={
                        <ProtectedRoute>
                          <OrganisationDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/documents"
                      element={
                        <ProtectedRoute requiredRole="tutor" allowProfessional={true}>
                          <DocumentLibrary />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/source-sets"
                      element={
                        <ProtectedRoute requiredRole="tutor" allowProfessional={true}>
                          <SourceSets />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/source-sets/:sourceSetId/ask"
                      element={
                        <ProtectedRoute requiredRole="tutor" allowProfessional={true}>
                          <SourceSetAsk />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/source-sets/:sourceSetId/summary"
                      element={
                        <ProtectedRoute requiredRole="tutor" allowProfessional={true}>
                          <SourceSetSummary />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/source-sets/:sourceSetId"
                      element={
                        <ProtectedRoute requiredRole="tutor" allowProfessional={true}>
                          <SourceSetDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requirePlatformAdmin={true}>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/accept-invite"
                      element={<AcceptInvite />}
                    />
                    <Route
                      path="/diagnostics"
                      element={
                        <ProtectedRoute>
                          <DiagnosticsPage />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </ErrorBoundary>
              </main>
            </div>
          </Router>
        </UpdateProvider>
      </AuthProvider>
    </DocumentProvider>
  );
}

function SystemErrorBanner() {
  const { systemError, clearSystemError, performApiTest } = useAuth();
  const [testStatus, setTestStatus] = React.useState<{ loading: boolean; result: string | null; success?: boolean }>({ loading: false, result: null });

  if (!systemError) return null;

  const handleTest = async () => {
    setTestStatus({ loading: true, result: null });
    const res = await performApiTest();
    setTestStatus({ loading: false, result: res.message, success: res.success });
  };

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-3">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-amber-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <div className="flex flex-col">
              <p className="text-sm font-medium">{systemError}</p>
              {testStatus.result && (
                <p className={`text-xs mt-1 ${testStatus.success ? 'text-green-400' : 'text-red-400'}`}>
                  Result: {testStatus.result}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleTest}
              disabled={testStatus.loading}
              className="text-xs px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full hover:bg-amber-500/30 transition-colors border border-amber-500/30 flex items-center gap-2 disabled:opacity-50"
            >
              {testStatus.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              {testStatus.success ? 'Test Successful' : 'Test AI Connection'}
            </button>
            <button
              onClick={clearSystemError}
              className="text-amber-400 hover:text-amber-300 p-1 rounded-md hover:bg-amber-500/10 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FirestoreOfflineBanner() {
  const { isOffline } = useAuth();

  if (!isOffline) return null;

  return (
    <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center gap-3 text-red-400">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.58 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
        </svg>
        <div className="flex flex-col">
          <p className="text-sm font-medium">Database connection issues detected. The system is operating in offline mode.</p>
          <p className="text-[10px] opacity-70 mt-0.5">Please check your internet connection or Firebase project settings if this persists. Some features may be restricted.</p>
        </div>
      </div>
    </div>
  );
}

function AuthErrorBanner() {
  const { authError, clearAuthError } = useAuth();

  if (!authError) return null;

  return (
    <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3 text-red-400">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <p className="text-sm font-medium">{authError}</p>
        </div>
        <button
          onClick={clearAuthError}
          className="text-red-400 hover:text-red-300 p-1 rounded-md hover:bg-red-500/10 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  );
}
